// ── AR Flow — NOVA INTERFACE: produtos e fotos DENTRO da conversa ────────────
// Abre pelo botão 🛋️ da barra de mensagem (todas as conversas: Leads,
// Presencial, Prospecção e Indicação) ou pelo "🔎 Parecidos" numa foto que o
// cliente mandou. O consultor acha o produto (catálogo) ou a foto real da loja
// (Biblioteca) e toca em "Usar esta foto": a foto vai para a barra de mensagem
// como PRÉVIA — nada é enviado sem o consultor tocar em Enviar.
// Fala só com o servidor do CRM (/api/produtos/* e /api/biblioteca).

import { useEffect, useState } from "react";
import { AVISO_VALOR, ControlesBuscaCatalogo, pedidoCatalogo, pedidoSimilarCatalogo, ResultadosCatalogo } from "./CatalogoBusca";
import { CATEGORIAS, EmPreparacao, lerJson, reduzirFoto, useFornecedoresStatus } from "./Produtos";
import FotoComPreco from "./FotoComPreco";
import useCatalogStatus from "./useCatalogStatus";

const NOME_LOCAL = v => String(v || "").toLowerCase().replace(/(^|\s)\S/g, s => s.toUpperCase());
export const EVENTO_PARECIDOS = "arflow:buscarParecidos";

// Pede ao painel da conversa para buscar parecidos com uma foto do cliente.
export function pedirParecidos(fotoUrl) {
  try { window.dispatchEvent(new CustomEvent(EVENTO_PARECIDOS, { detail: { fotoUrl } })); } catch { /* navegador antigo */ }
}

// Baixa uma imagem (direto; se o navegador bloquear, pelo servidor do CRM).
export async function baixarImagem(url) {
  const tentar = async u => { const r = await fetch(u, { credentials: u.startsWith("/") ? "include" : "omit" }); if (!r.ok) throw new Error("falhou"); const b = await r.blob(); if (!/^image\//.test(b.type)) throw new Error("não é imagem"); return b; };
  if (!url) throw new Error("Foto sem endereço.");
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("/")) return tentar(url);
  try { return await tentar(url); }
  catch { return tentar(`/api/produtos/imagem?u=${encodeURIComponent(url)}`); }
}

export function nomeArquivo(texto) {
  const base = String(texto || "produto").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "produto";
  return `${base.slice(0, 60)}.jpg`;
}

function FotoEscolhivel({ th, url, rotulo, detalhe, onUsar, usando }) {
  const line = th.nuLine || th.sandDark;
  return (
    <div style={{ border: `1px solid ${line}`, borderRadius: 4, overflow: "hidden", background: "#fff", display: "flex", flexDirection: "column" }}>
      <img src={url} alt={rotulo || ""} loading="lazy" style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: th.sandDark }} />
      <div style={{ padding: "7px 8px 8px", display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
        {rotulo && <div style={{ fontWeight: 700, fontSize: 13, color: th.text }}>{rotulo}</div>}
        {detalhe && <div style={{ fontSize: 11, color: th.muted }}>{detalhe}</div>}
        <button type="button" onClick={onUsar} disabled={usando} style={{ marginTop: "auto", border: "none", borderRadius: 4, padding: "7px 8px", background: th.accent, color: "#fff", fontWeight: 700, fontSize: 12, cursor: usando ? "default" : "pointer", fontFamily: "inherit", opacity: usando ? 0.6 : 1 }}>
          {usando ? "Preparando…" : "Usar esta foto"}
        </button>
      </div>
    </div>
  );
}

export default function PainelProdutosConversa({ theme, fotoInicial = null, onFechar, onUsarImagem }) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const [aba, setAba] = useState("produtos");
  const statusConexao = useCatalogStatus();
  const conectado = statusConexao.conectado;
  const [erro, setErro] = useState("");
  const [usando, setUsando] = useState("");
  const [editor, setEditor] = useState(null); // foto escolhida, antes de ir para a mensagem
  // Produtos
  const [texto, setTexto] = useState("");
  const [medida, setMedida] = useState("");
  const [fornecedor, setFornecedor] = useState("");
  const [foto, setFoto] = useState(null);
  const fornecedorStatus = useFornecedoresStatus();
  const fornecedores = fornecedorStatus.fornecedores;
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [fotosDoProduto, setFotosDoProduto] = useState([]);
  // Biblioteca
  const [bib, setBib] = useState({ q: "", local: "", categoria: "" });
  const [fotosBib, setFotosBib] = useState([]);
  const [carregandoBib, setCarregandoBib] = useState(false);
  // Foto que o cliente mandou (botão "🔎 Parecidos"): já prepara a busca por foto.
  useEffect(() => {
    if (!fotoInicial) return;
    let vivo = true;
    setAba("produtos");
    baixarImagem(fotoInicial)
      .then(b => reduzirFoto(new File([b], "cliente.jpg", { type: b.type })))
      .then(d => vivo && setFoto(d))
      .catch(() => vivo && setErro("Não consegui abrir a foto do cliente. Salve a imagem e use “📷 Foto”."));
    return () => { vivo = false; };
  }, [fotoInicial]);

  useEffect(() => {
    if (!conectado || aba !== "loja") return;
    let vivo = true;
    setCarregandoBib(true);
    const p = new URLSearchParams(Object.entries(bib).filter(([, v]) => v));
    const t = setTimeout(() => {
      fetch(`/api/biblioteca?${p}`, { credentials: "include" }).then(lerJson)
        .then(d => vivo && setFotosBib(d.fotos || [])).catch(e => vivo && setErro(e.message)).finally(() => vivo && setCarregandoBib(false));
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [conectado, aba, bib]);

  useEffect(() => {
    if (!aberto) { setFotosDoProduto([]); return; }
    let vivo = true;
    setFotosDoProduto(aberto.fotoUrl ? [aberto.fotoUrl] : []);
    const produtoId = aberto.isBiblioteca ? aberto.produtoId : aberto.id;
    if (!produtoId) return () => { vivo = false; };
    fetch(`/api/produtos/item/${encodeURIComponent(produtoId)}`, { credentials: "include" }).then(lerJson)
      .then(d => { const p = d.produto || d; if (vivo && Array.isArray(p.fotos) && p.fotos.length) setFotosDoProduto(p.fotos.slice(0, 8)); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [aberto]);

  const enviarBusca = async pedido => {
    setBuscando(true); setErro(""); setResultado(null); setAberto(null);
    try {
      const r = await fetch("/api/produtos/busca", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(pedido) });
      setResultado(await lerJson(r));
    } catch (err) { setErro(err.message); } finally { setBuscando(false); }
  };
  const buscar = () => {
    if (!texto.trim() && !foto && !fornecedor) { setErro("Descreva o que procura ou escolha uma foto."); return; }
    return enviarBusca(pedidoCatalogo({ texto, fornecedor, medida, foto, limite: 20 }));
  };
  const buscarSimilar = produto => enviarBusca(pedidoSimilarCatalogo(produto, { fornecedor, medida, limite: 20 }));

  const usar = async (url, legenda) => {
    if (usando) return;
    setUsando(url); setErro("");
    try {
      const blob = await baixarImagem(url);
      setEditor({ blob, legenda: legenda || "" });
    } catch { setErro("Não consegui preparar essa foto para envio. Tente outra."); } finally { setUsando(""); }
  };

  const campo = { padding: "7px 9px", borderRadius: 4, border: `1px solid ${line}`, fontFamily: "inherit", fontSize: 13, background: "#fff", color: th.text, minWidth: 0 };
  const botaoAba = ativo => ({ border: "none", borderBottom: `3px solid ${ativo ? th.accent : "transparent"}`, background: "none", padding: "9px 2px", marginRight: 16, fontSize: 14, fontWeight: ativo ? 700 : 500, color: ativo ? th.text : th.muted, cursor: "pointer", fontFamily: "inherit" });
  const grade = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 10 };

  return (
    <div role="dialog" aria-modal="true" aria-label="Produtos e fotos" onClick={e => { e.stopPropagation(); onFechar(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 1200, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 100%)", height: "100%", overflowY: "auto", background: th.sand || "#fff", padding: "14px 16px 24px", boxSizing: "border-box", color: th.text, fontFamily: "inherit" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div role="tablist">
            <button role="tab" aria-selected={aba === "produtos"} type="button" onClick={() => setAba("produtos")} style={botaoAba(aba === "produtos")}>🔎 Produtos</button>
            <button role="tab" aria-selected={aba === "loja"} type="button" onClick={() => setAba("loja")} style={botaoAba(aba === "loja")}>📷 Fotos da loja</button>
          </div>
          <button type="button" onClick={onFechar} style={{ ...campo, cursor: "pointer" }}>Fechar</button>
        </div>
        <div style={{ fontSize: 12, color: th.muted, margin: "6px 0 10px" }}>A foto escolhida vai para a mensagem como prévia. Só é enviada quando você tocar em Enviar.</div>
        <div role="note" style={{ fontSize: 12, color: th.muted, margin: "0 0 10px" }}>{AVISO_VALOR}</div>

        {editor && (
          <FotoComPreco theme={th} blob={editor.blob} legenda={editor.legenda}
            onCancelar={() => setEditor(null)}
            onConfirmar={(arquivo, legenda) => {
              if (onUsarImagem(arquivo, legenda || "") === false) return false;
              setEditor(null);
              onFechar();
              return true;
            }} />
        )}
        {(statusConexao.state === "verificando" || statusConexao.state === "retentando") && (
          <div role="status" style={{ margin: "8px 0", color: th.muted, fontSize: 13 }}>
            {statusConexao.state === "retentando"
              ? "A verificação demorou ou falhou; tentando novamente…"
              : "Verificando a conexão do catálogo…"}
          </div>
        )}
        {statusConexao.state === "falha" && (
          <div role="alert" style={{ margin: "8px 0", color: "#991B1B", fontSize: 13 }}>
            Não foi possível verificar a conexão do catálogo: {statusConexao.error}
          </div>
        )}
        {conectado === false && <EmPreparacao th={th} titulo="Catálogo em preparação" texto="O servidor confirmou que o catálogo não está conectado. Quando for conectado, você acha produtos e fotos da loja aqui mesmo, sem sair da conversa." />}
        {(statusConexao.state === "desconectado" || statusConexao.state === "falha") && (
          <button
            type="button"
            onClick={statusConexao.retry}
            style={{ marginTop: 8, border: `1px solid ${line}`, borderRadius: 4, background: "#fff", color: th.text, padding: "7px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >Verificar catálogo novamente</button>
        )}
        {fornecedorStatus.carregando && <div aria-live="polite" style={{ margin: "8px 0", color: th.muted, fontSize: 13 }}>Carregando fornecedores…</div>}
        {fornecedorStatus.erro && (
          <div style={{ margin: "8px 0" }}>
            <div role="alert" style={{ color: "#991B1B", fontSize: 13 }}>Não foi possível carregar os fornecedores: {fornecedorStatus.erro}</div>
            <button type="button" onClick={fornecedorStatus.retry} style={{ marginTop: 5, border: `1px solid ${line}`, borderRadius: 4, background: "#fff", color: th.text, padding: "7px 10px", cursor: "pointer" }}>Tentar carregar fornecedores novamente</button>
          </div>
        )}
        {erro && <div role="alert" style={{ margin: "8px 0", background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", borderRadius: 4, padding: "8px 10px", fontSize: 13 }}>{erro}</div>}

        {conectado && aba === "produtos" && !aberto && (
          <>
            <ControlesBuscaCatalogo compact texto={texto} onTexto={setTexto} fornecedores={fornecedores}
              fornecedor={fornecedor} onFornecedor={setFornecedor} medida={medida} onMedida={setMedida}
              onBuscar={buscar} buscando={buscando} foto={foto}
              onFoto={(data, error) => { if (error) setErro(error.message); else { setFoto(data); setErro(""); } }}
              onRemoverFoto={() => setFoto(null)} reduzirFoto={reduzirFoto} />
            {resultado && <ResultadosCatalogo compact theme={th} resposta={resultado} onDetalhes={setAberto} onSimilares={buscarSimilar} />}
          </>
        )}

        {conectado && aba === "produtos" && aberto && (
          <div>
            <button type="button" onClick={() => setAberto(null)} style={{ ...campo, cursor: "pointer", marginBottom: 8 }}>← Voltar aos resultados</button>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{aberto.nome}</div>
            <div style={{ fontSize: 12, color: th.muted }}>{aberto.fornecedor || ""}</div>
            <div style={{ fontSize: 12, color: th.muted, marginTop: 6 }}>{AVISO_VALOR}</div>
            {aberto.textoVenda && <p style={{ fontSize: 13, lineHeight: 1.5 }}>{aberto.textoVenda}</p>}
            {!fotosDoProduto.length && <div style={{ marginTop: 10, fontSize: 13, color: th.muted }}>Este produto não tem foto.</div>}
            <div style={grade}>
              {fotosDoProduto.map(u => <FotoEscolhivel key={u} th={th} url={u} onUsar={() => usar(u, aberto.nome)} usando={usando === u} />)}
            </div>
          </div>
        )}

        {conectado && aba === "loja" && (
          <>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input aria-label="Buscar produto" value={bib.q} onChange={e => setBib(b => ({ ...b, q: e.target.value }))} placeholder="Buscar produto" style={{ ...campo, flex: "1 1 150px" }} />
              <select aria-label="Local" value={bib.local} onChange={e => setBib(b => ({ ...b, local: e.target.value }))} style={campo}>
                <option value="">Todos os locais</option><option value="SHOPPING">Shopping</option><option value="MEZZO">Mezzo</option><option value="DECORACAO">Decoração</option>
              </select>
              <select aria-label="Categoria" value={bib.categoria} onChange={e => setBib(b => ({ ...b, categoria: e.target.value }))} style={campo}>
                {CATEGORIAS.map(([v, n]) => <option key={v} value={v}>{v ? n : "Todas"}</option>)}
              </select>
            </div>
            {carregandoBib && <div style={{ marginTop: 10, fontSize: 13, color: th.muted }}>Carregando fotos…</div>}
            {!carregandoBib && !fotosBib.length && <div style={{ marginTop: 10, fontSize: 13, color: th.muted }}>Nenhuma foto com esses filtros.</div>}
            <div style={grade}>
              {fotosBib.map(f => (
                <FotoEscolhivel key={f.id} th={th} url={f.miniaturaUrl || f.url} rotulo={f.produtoNome}
                   detalhe={f.local ? `📍 ${NOME_LOCAL(f.local)}` : null} onUsar={() => usar(f.url || f.miniaturaUrl, f.produtoNome, null)} usando={usando === (f.url || f.miniaturaUrl)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
